import { useEffect, useRef, useState } from "react";
import {
  Button, Form, Input, Modal, Select, Space, Switch,
  Table, Tag, Tooltip, Typography, message
} from "antd";
import { EditOutlined, PauseCircleOutlined, PlayCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { ServicesHospital } from "../services/ServicesHospital";

const { TextArea } = Input;
const { Title } = Typography;

// Debe coincidir exactamente con ACCIONES_CLASIFICACION en icot/api/views.py
// y con lo que espera icot/clasificacion/clasificador.py.
const ACCIONES = [
  { value: "responder_cortar", label: "Responder con audio y cortar" },
  { value: "responder_transferir", label: "Responder con audio y transferir a número (parámetro)" },
  { value: "transferir", label: "Transferir a número (parámetro)" },
];

const accionLabel = (valor) => ACCIONES.find(a => a.value === valor)?.label || valor;
const usaAudio = (accion) => accion === "responder_cortar" || accion === "responder_transferir";
const usaTelefono = (accion) => accion === "responder_transferir" || accion === "transferir";

const categoriaVacia = {
  codigo: "",
  departamento: "",
  pregunta_tipo: "",
  respuesta: "",
  accion: "transferir",
  audio: "",
  telefono_transferencia: "",
  cola_znuny: "",
  nota_transferencia: "",
  sensible: false,
  activo: true,
  confirmar: false,
  automatizada: true,
};

/**
 * Component HospitalCategorias
 *
 * Administración de la taxonomía de categorías que usa el clasificador de
 * llamadas (tabla coflpa_categorias). Permite añadir una categoría nueva o
 * cambiar los datos de una existente (departamento, acción, audio, teléfono
 * de transferencia, cola de Znuny...) sin necesidad de desplegar código: el
 * clasificador relee la tabla como máximo cada 60 segundos.
 * @returns
 */
export const HospitalCategorias = () => {

  ////////
  // Hooks
  ////////
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [audioSonando, setAudioSonando] = useState(null);
  const audioRef = useRef(null);

  // Para mostrar/ocultar audio y teléfono según la acción elegida y si se
  // ha marcado "confirmar" (ver usaTelefono más abajo)
  const accionSeleccionada = Form.useWatch("accion", form);
  const confirmarSeleccionado = Form.useWatch("confirmar", form);

  useEffect(() => {
    document.title = "COFLPA - categorías";
    getCategorias();
    return () => audioRef.current?.pause();
  }, []);

  ////////////
  // Functions
  ////////////
  const getCategorias = async () => {
    setLoading(true);
    try {
      const data = await ServicesHospital.getCategorias();
      setCategorias(data || []);
    } finally {
      setLoading(false);
    }
  };

  const toggleAudio = (ruta) => {
    audioRef.current?.pause();
    if (audioSonando === ruta) {
      setAudioSonando(null);
      return;
    }
    const audio = new Audio(ServicesHospital.getAudioUrl(ruta));
    audio.onended = () => setAudioSonando(null);
    audio.onerror = () => {
      setAudioSonando(null);
      messageApi.error("No se pudo reproducir el audio");
    };
    audioRef.current = audio;
    setAudioSonando(ruta);
    audio.play().catch(() => {});
  };

  const openNueva = () => {
    setEditando(false);
    form.resetFields();
    form.setFieldsValue(categoriaVacia);
    setModalOpen(true);
  };

  const openEditar = (categoria) => {
    setEditando(true);
    form.resetFields();
    form.setFieldsValue(categoria);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const guardar = async (values) => {
    try {
      if (editando) {
        await ServicesHospital.updateCategoria(values.codigo, values);
      } else {
        const resp = await ServicesHospital.createCategoria(values);
        if (resp?.error) {
          messageApi.error(resp.error);
          return;
        }
      }
      messageApi.success(editando ? "Categoría actualizada" : "Categoría creada");
      setModalOpen(false);
      getCategorias();
    } catch (e) {
      messageApi.error("No se pudo guardar la categoría");
    }
  };

  const cambiarActivo = async (categoria, activo) => {
    await ServicesHospital.updateCategoria(categoria.codigo, { activo });
    getCategorias();
  };

  const cambiarAutomatizada = async (categoria, automatizada) => {
    await ServicesHospital.updateCategoria(categoria.codigo, { automatizada });
    getCategorias();
  };

  const columns = [
    {
      title: 'Código',
      dataIndex: 'codigo',
      key: 'codigo',
      width: 110,
      fixed: 'left',
      sorter: (a, b) => a.codigo.localeCompare(b.codigo),
    },
    {
      title: 'Departamento',
      dataIndex: 'departamento',
      key: 'departamento',
      width: 160,
    },
    {
      title: 'Cuándo se usa',
      dataIndex: 'pregunta_tipo',
      key: 'pregunta_tipo',
      ellipsis: { showTitle: false },
      render: (value) => (
        <Tooltip title={value}>{value}</Tooltip>
      ),
    },
    {
      title: 'Acción',
      dataIndex: 'accion',
      key: 'accion',
      width: 230,
      filters: ACCIONES.map(a => ({ text: a.label, value: a.value })),
      onFilter: (value, record) => record.accion === value,
      render: (value) => <Tag color="blue">{accionLabel(value)}</Tag>,
    },
    {
      title: 'Audio',
      dataIndex: 'audio',
      key: 'audio',
      width: 160,
      render: (value) => value
        ? (
          <>
            {value}{' '}
            <Button
              type="text"
              size="small"
              icon={audioSonando === value
                ? <PauseCircleOutlined style={{ fontSize: 18 }} />
                : <PlayCircleOutlined style={{ fontSize: 18 }} />}
              onClick={() => toggleAudio(value)}
            />
          </>
        )
        : <Tag color="default">sin audio</Tag>,
    },
    {
      title: 'Teléfono',
      dataIndex: 'telefono_transferencia',
      key: 'telefono_transferencia',
      width: 120,
      render: (value) => value || <Tag color="default">sin teléfono</Tag>,
    },
    {
      title: 'Cola Znuny',
      dataIndex: 'cola_znuny',
      key: 'cola_znuny',
      width: 140,
    },
    {
      title: 'Sensible',
      dataIndex: 'sensible',
      key: 'sensible',
      width: 90,
      align: 'center',
      render: (value) => value ? <Tag color="volcano">Sí</Tag> : '',
    },
    {
      title: 'Confirma',
      dataIndex: 'confirmar',
      key: 'confirmar',
      width: 100,
      align: 'center',
      render: (value, record) => (record.accion === 'responder_cortar' && value)
        ? <Tag color="purple">Sí</Tag>
        : '',
    },
    {
      title: 'Automatizada',
      dataIndex: 'automatizada',
      key: 'automatizada',
      width: 120,
      align: 'center',
      render: (value, record) => (
        <Switch checked={value} disabled={!record.activo}
          onChange={(checked) => cambiarAutomatizada(record, checked)} />
      ),
    },
    {
      title: 'Activa',
      dataIndex: 'activo',
      key: 'activo',
      width: 90,
      align: 'center',
      render: (value, record) => (
        <Switch checked={value} onChange={(checked) => cambiarActivo(record, checked)} />
      ),
    },
    {
      title: '',
      key: 'acciones',
      width: 60,
      fixed: 'right',
      render: (_, record) => (
        <Button shape="circle" icon={<EditOutlined />} onClick={() => openEditar(record)} />
      ),
    },
  ];

  return (
    <div style={{ margin: 10 }}>
      {contextHolder}
      <Space style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>Categorías del clasificador</Title>
        <Button type="primary" icon={<PlusOutlined />} onClick={openNueva}>
          Nueva categoría
        </Button>
      </Space>

      <div style={{ backgroundColor: "#FFF", overflow: "auto" }}>
        <Table
          rowKey="codigo"
          dataSource={categorias}
          columns={columns}
          loading={loading}
          bordered
          scroll={{ x: 1400 }}
          pagination={{ pageSize: 20 }}
        />
      </div>

      <Modal
        title={editando ? "Editar categoría" : "Nueva categoría"}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => form.submit()}
        okText={editando ? "Guardar cambios" : "Crear categoría"}
        cancelText="Cancelar"
        width={700}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={guardar}
          initialValues={categoriaVacia}
        >
          <Form.Item
            label="Código"
            name="codigo"
            rules={[{ required: true, message: "El código es obligatorio" }]}
          >
            <Input placeholder="Ej. INFO-19" disabled={editando} />
          </Form.Item>

          <Form.Item
            label="Departamento"
            name="departamento"
            rules={[{ required: true, message: "El departamento es obligatorio" }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Cuándo se usa (esto es lo que lee Gemini para clasificar la llamada)"
            name="pregunta_tipo"
            rules={[{ required: true, message: "Campo obligatorio" }]}
          >
            <TextArea rows={3} />
          </Form.Item>

          <Form.Item
            label="Respuesta / resumen (uso interno, opcional)"
            name="respuesta"
          >
            <TextArea rows={2} />
          </Form.Item>

          <Form.Item
            label="Acción"
            name="accion"
            rules={[{ required: true, message: "Selecciona una acción" }]}
          >
            <Select options={ACCIONES} />
          </Form.Item>

          {usaAudio(accionSeleccionada) && (
            <Form.Item
              label="Audio de respuesta (ruta dentro del servidor de audios)"
              name="audio"
              rules={[{ required: true, message: "Esta acción necesita un audio" }]}
              extra="Si la ruta queda vacía o el audio no existe, la llamada se comporta como si la categoría no tuviera acción configurada."
            >
              <Input placeholder="Ej. coflpa/respuestas/info-19.wav" />
            </Form.Item>
          )}

          {accionSeleccionada === "responder_cortar" && (
            <Form.Item
              label="Confirmar antes de colgar"
              name="confirmar"
              valuePropName="checked"
              extra='Si está activo, después del audio se pregunta "¿se ha resuelto su consulta?": si la persona dice que sí, se corta la llamada; si dice que no, se transfiere al teléfono de abajo (si tiene uno puesto) o al reparto genérico por horario.'
            >
              <Switch />
            </Form.Item>
          )}

          {(usaTelefono(accionSeleccionada) || (accionSeleccionada === "responder_cortar" && confirmarSeleccionado)) && (
            <Form.Item
              label="Teléfono de transferencia"
              name="telefono_transferencia"
              rules={usaTelefono(accionSeleccionada)
                ? [{ required: true, message: "Esta acción necesita un teléfono" }]
                : []}
              extra={accionSeleccionada === "responder_cortar"
                ? "Opcional: solo se usa si la persona dice que no se ha resuelto su consulta. Si se deja en blanco, en ese caso se transfiere al reparto genérico por horario."
                : undefined}
            >
              <Input placeholder="Ej. 928123456" />
            </Form.Item>
          )}

          <Form.Item
            label="Cola de Znuny"
            name="cola_znuny"
            extra="Se usa para etiquetar el ticket que se genere a partir de esta llamada."
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Nota de transferencia (se añade a las notas del caso)"
            name="nota_transferencia"
          >
            <TextArea rows={2} />
          </Form.Item>

          <Space size="large">
            <Form.Item label="Sensible" name="sensible" valuePropName="checked"
              extra="Si está marcada, nunca se responde ni corta con audio: siempre se transfiere."
            >
              <Switch />
            </Form.Item>

            <Form.Item label="Activa" name="activo" valuePropName="checked"
              extra="Solo las categorías activas se ofrecen al clasificador."
            >
              <Switch />
            </Form.Item>

            <Form.Item label="Automatizada" name="automatizada" valuePropName="checked"
              extra="Si está desactivada, la llamada se sigue clasificando en esta categoría, pero no se ejecuta ninguna acción (ni audio, ni transferencia, ni confirmar): va al reparto genérico por horario."
            >
              <Switch />
            </Form.Item>
          </Space>
        </Form>
      </Modal>
    </div>
  );
}
